import { DroApiService } from './dro-api.service';
import { DroServiceOrderState } from './dro.models';

/**
 * The service order popups' DRO part (docs/DRO 4.1, 4.6), shared by the Conway and ISC popups: the tab's state (the tab
 * exists only when the order's model has an active form; red dot while the reading is outstanding, green tick once
 * this order's reading is saved) and the Payment / Invoice check, which asks the API each time (completion-check) and
 * shows the block with "Go to DRO tab" when the reading is outstanding.
 */
export class DroOrderTab {
  state: DroServiceOrderState | null = null;
  block: { action: 'Payment' | 'Invoice'; message: string | null } | null = null;
  checking = false;

  constructor(private readonly api: DroApiService, private readonly notify: (message: string) => void) {}

  get shown(): boolean { return !!this.state?.form; }
  get outstanding(): boolean { return !!this.state?.outstanding; }
  get recorded(): boolean { return !!this.state?.readingNo; }

  private loadedFor: string | null = null;

  load(serviceOrderId: string | null | undefined) {
    if ((serviceOrderId ?? null) === this.loadedFor) return;
    this.loadedFor = serviceOrderId ?? null;
    this.state = null;
    if (!serviceOrderId) return;
    // A 403 (no DRO tab for this role) or any failure: no tab; the Payment / Invoice check still asks on its own.
    this.api.serviceOrder(serviceOrderId).subscribe({
      next: s => { if (this.loadedFor === serviceOrderId) this.state = s; },
      error: () => { if (this.loadedFor === serviceOrderId) this.state = null; },
    });
  }

  /** Runs `proceed` unless the reading is outstanding; then the block is shown instead. */
  check(serviceOrderId: string | null | undefined, action: 'Payment' | 'Invoice', proceed: () => void) {
    if (!serviceOrderId) { proceed(); return; }
    if (this.checking) return;
    this.checking = true;
    this.api.completionCheck(serviceOrderId).subscribe({
      next: result => {
        this.checking = false;
        if (result.blocked) this.block = { action, message: result.message ?? null };
        else proceed();
      },
      error: e => {
        this.checking = false;
        this.notify(DroApiService.message(e, `The DRO reading check failed, so ${action} was not started. Please try again.`));
      },
    });
  }
}
